import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Building2 } from 'lucide-react';

interface FooterProps {
  onScrollToSection?: (sectionId: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onScrollToSection }) => {
  const { t } = useTranslation('portal');

  const scrollToSection = (sectionId: string, event?: React.MouseEvent) => {
    event?.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

    if (onScrollToSection) {
      onScrollToSection(sectionId);
    }
  };

  const quickLinks = [
    { id: 'home', label: t('nav.home'), href: '#home' },
    { id: 'pay-invoice', label: t('nav.payInvoice'), href: '#pay-invoice' },
    { id: 'for-companies', label: t('nav.forCompanies'), href: '#for-companies' },
    { id: 'about', label: t('nav.about'), href: '#about' },
    { id: 'contact', label: t('nav.contact'), href: '#contact' },
  ];

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8 text-center md:text-left">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-2xl font-bold text-blue-400 mb-4">FiskalPay</h3>
            <p className="text-slate-400 text-sm">
              {t('footer.description')}
            </p>
          </div>

          {/* Merchant / Legal Info */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t('footer.merchantInfo')}
            </h4>
            <div className="space-y-1.5 text-sm text-slate-400 flex flex-col items-center md:items-start">
              <p className="font-medium text-slate-300">
                "S-TEN" Saša Dreković s.p. Banja Luka
              </p>
              <div className="flex items-start justify-center md:justify-start gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Kordunaška 4, 78000 Banja Luka</span>
              </div>
              <p>JIB: 4512033480003</p>
              <p>PIB: 512033480003</p>
              <p className="break-all">{t('footer.bankLabel')}: Intesa Sanpaolo Banka BiH</p>
              <p className="break-all">Žiro račun: 154-560-20193540-81</p>
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4">{t('footer.contact')}</h4>
            <div className="space-y-2 text-sm text-slate-400 flex flex-col items-center md:items-start">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:sasa.drekovic@gmail.com" className="hover:text-blue-400 transition-colors break-all">
                  sasa.drekovic@gmail.com
                </a>
              </div>
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a href="tel:+38765830040" className="hover:text-blue-400 transition-colors">
                  +387 65 830 040
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(link.id, e)}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Logos */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex items-center justify-center gap-5 flex-wrap mb-6">
            <a href="https://www.mastercard.ba/bs-ba.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/mc-id-check.webp" alt="Mastercard ID Check" height="30" className="h-[30px] w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
            <a href="https://www.visa.co.uk/about-visa/visa-in-europe.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/visa-secure.webp" alt="Visa Secure" height="30" className="h-[30px] w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
            <img src="/images/payment/payweb.webp" alt="PayWeb" height="30" className="h-[30px] w-auto opacity-90" />
            <a href="https://www.mastercard.ba/bs-ba.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/mc.webp" alt="Mastercard" height="30" className="h-[30px] w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
            <a href="https://brand.mastercard.com/brandcenter/more-about-our-brands.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/maestro.webp" alt="Maestro" height="30" className="h-[30px] w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
            <a href="https://www.visa.co.uk/about-visa/visa-in-europe.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/visa.webp" alt="Visa" height="30" className="h-[30px] w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500 text-center md:text-left">
              © {new Date().getFullYear()} "S-TEN" Saša Dreković s.p. Banja Luka. {t('footer.rightsReserved')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-blue-400 transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <span>•</span>
              <Link to="/terms" className="hover:text-blue-400 transition-colors">
                {t('footer.termsOfService')}
              </Link>
              <span>•</span>
              <Link to="/cookies" className="hover:text-blue-400 transition-colors">
                {t('footer.cookiePolicy')}
              </Link>
              <span>•</span>
              <Link to="/refund-policy" className="hover:text-blue-400 transition-colors">
                {t('footer.refundPolicy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
